import {
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  MapPin,
  Search,
  Sparkles,
  UserX,
  UsersRound,
  Video,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getAttendanceRecords, getPublicAcademyBySlug, getStudentCourseSignups, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatDate, formatDateTime } from '../../utils/date';
import { getHostnameContext } from '../../utils/hostname';
import { StudentSidebar } from './StudentSidebar';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function attendanceStatus(record) {
  return String(record.status || record.attendanceStatus || (record.present ? 'present' : 'absent')).toLowerCase();
}

function statusLabel(status) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function statusIcon(status) {
  return status === 'present' || status === 'late' ? CheckCircle2 : UserX;
}

function modeDetails(record) {
  if (record.locationType === 'physical') return { label: 'On campus', icon: MapPin };
  if (record.locationType === 'hybrid') return { label: 'Hybrid', icon: UsersRound };
  return { label: 'Virtual', icon: Video };
}

export function StudentAttendancePage() {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const signups = await getStudentCourseSignups(user.uid);
        let scoped = signups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? signups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId || signup.ref?.parent?.parent?.id).filter(Boolean))];
        const groups = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenant = await getTenant(tenantId);
          const signupsForTenant = scoped.filter((signup) => (signup.tenantId || signup.ref?.parent?.parent?.id) === tenantId);
          const identifiers = new Set([user.uid, user.email, ...signupsForTenant.flatMap((signup) => [signup.id, signup.studentId, signup.email, signup.authEmail, signup.authUid])]
            .filter(Boolean)
            .map((value) => String(value).toLowerCase()));
          const attendance = await getAttendanceRecords(tenantId);
          return attendance.filter((record) => [record.studentId, record.authUid, record.studentEmail, record.email]
            .filter(Boolean)
            .some((value) => identifiers.has(String(value).toLowerCase())))
            .map((record) => ({ ...record, tenant }));
        }));
        if (mounted) {
          const nextRecords = groups.flat().sort((a, b) => (toDate(b.date || b.attendanceDate || b.createdAt) || 0) - (toDate(a.date || a.attendanceDate || a.createdAt) || 0));
          setRecords(nextRecords);
          setAcademyName(nextRecords[0]?.tenant?.name || 'My learning space');
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load attendance reports', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, user?.email, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const present = records.filter((record) => ['present', 'late'].includes(attendanceStatus(record))).length;
  const absent = records.filter((record) => attendanceStatus(record) === 'absent').length;
  const excused = records.filter((record) => attendanceStatus(record) === 'excused').length;
  const rate = records.length ? Math.round((present / records.length) * 100) : 0;
  const normalizedQuery = query.trim().toLowerCase();
  const filteredRecords = records.filter((record) => {
    const status = attendanceStatus(record);
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'present' && ['present', 'late'].includes(status))
      || status === statusFilter;
    const matchesQuery = !normalizedQuery || [record.className, record.title, record.courseName, record.tenant?.name, status]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
    return matchesStatus && matchesQuery;
  });
  const firstName = String(user.displayName || user.email || 'Student').split(/[ @]/)[0];
  const dateLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());

  return (
    <main className="student-portal student-attendance-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-attendance-shell">
        <section className="student-attendance-workspace">
          <header className="student-attendance-page-header">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>Attendance</h1>
              <p>{dateLabel}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          <div className="student-attendance-metrics-new">
            <article><span className="is-violet"><ClipboardCheck size={18} /></span><div><small>Sessions recorded</small><strong>{records.length}</strong></div></article>
            <article><span className="is-blue"><CheckCircle2 size={18} /></span><div><small>Present or late</small><strong>{present}</strong></div></article>
            <article><span className="is-red"><UserX size={18} /></span><div><small>Absent</small><strong>{absent}</strong></div></article>
            <article><span className="is-gold"><CalendarDays size={18} /></span><div><small>Excused</small><strong>{excused}</strong></div></article>
          </div>

          <div className="student-attendance-toolbar-new">
            <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search attendance records" /></label>
            <div className="student-attendance-filters" aria-label="Filter attendance">
              <button className={statusFilter === 'all' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('all')}>All</button>
              <button className={statusFilter === 'present' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('present')}>Present</button>
              <button className={statusFilter === 'absent' ? 'is-active' : ''} type="button" onClick={() => setStatusFilter('absent')}>Absent</button>
            </div>
          </div>

          <div className="student-attendance-section-heading">
            <div><h2>Attendance history</h2><p>Your recorded participation across all enrolled classes.</p></div>
            <span>{filteredRecords.length} record{filteredRecords.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="student-courses-inline-loading"><span /><p>Loading attendance...</p></div>
          ) : filteredRecords.length ? (
            <div className="student-attendance-records">
              <div className="student-attendance-record-head"><span>Session</span><span>Class mode</span><span>Date</span><span>Status</span></div>
              {filteredRecords.map((record) => {
                const status = attendanceStatus(record);
                const StatusIcon = statusIcon(status);
                const mode = modeDetails(record);
                const ModeIcon = mode.icon;
                const recordDate = toDate(record.date || record.attendanceDate || record.createdAt);
                return (
                  <article key={`${record.tenant?.id}-${record.id}`}>
                    <div className="student-attendance-session"><span><CalendarDays size={16} /></span><div><strong>{record.className || record.title || record.courseName || 'Class session'}</strong><small>{record.tenant?.name || academyName}</small></div></div>
                    <div className="student-attendance-mode"><ModeIcon size={14} /><span>{mode.label}</span></div>
                    <div className="student-attendance-date"><strong>{recordDate ? formatDate(recordDate) : '—'}</strong><small>{toDate(record.createdAt) ? formatDateTime(record.createdAt) : 'Recorded attendance'}</small></div>
                    <span className={`student-attendance-pill student-attendance-pill--${status}`}><StatusIcon size={13} />{statusLabel(status)}</span>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="student-courses-empty">
              <ClipboardCheck size={25} />
              <h2>{query || statusFilter !== 'all' ? 'No matching records' : 'No attendance records yet'}</h2>
              <p>{query || statusFilter !== 'all' ? 'Try another search or attendance status.' : 'Your report will appear here after your academy records your first class session.'}</p>
              {query || statusFilter !== 'all' ? <button type="button" onClick={() => { setQuery(''); setStatusFilter('all'); }}>Clear filters</button> : null}
            </div>
          )}
        </section>

        <aside className="student-attendance-overview">
          <div className="student-attendance-overview-heading"><h2>Participation</h2><ClipboardCheck size={17} /></div>
          <div className="student-attendance-rate" style={{ '--attendance-rate': `${rate * 3.6}deg` }}>
            <div><strong>{rate}%</strong><small>Attendance rate</small></div>
          </div>
          <div className="student-attendance-breakdown">
            <div><span><i className="is-blue" />Present or late</span><strong>{present}</strong></div>
            <div><span><i className="is-red" />Absent</span><strong>{absent}</strong></div>
            <div><span><i className="is-gold" />Excused</span><strong>{excused}</strong></div>
          </div>
          <div className="student-attendance-note">
            <span><Sparkles size={16} /></span>
            <small>Consistency matters</small>
            <h3>Every class builds momentum.</h3>
            <p>Regular participation makes it easier to retain concepts and stay connected to your course.</p>
          </div>
          <Link className="student-attendance-class-link" to="/student/classes"><Clock3 size={15} />View my classes<ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
